import React, { useState, useEffect } from 'react';
import { Calendar, ChevronRight, Users } from 'react-feather';
import { tournamentAPI, Tournament } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import TournamentListModal from './TournamentListModal';

interface TournamentSectionProps {
  athleteId: string;
}

const TournamentSection: React.FC<TournamentSectionProps> = ({ athleteId }) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (sessionToken && athleteId) {
      loadTournaments();
    }
  }, [sessionToken, athleteId]);

  const loadTournaments = async () => {
    if (!sessionToken || !athleteId) return;

    try {
      const response = await tournamentAPI.getTournaments(
        sessionToken,
        athleteId,
        'upcoming'
      );

      if (response.success) {
        setTournaments(response.data.tournaments);
      }
    } catch (error) {
      console.error('Error loading tournaments:', error);
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const upcomingCount = tournaments.length;
  const totalGames = tournaments.reduce((sum, t) => sum + (t.games?.length || 0), 0);
  const nextTournament = tournaments[0]; // Already sorted by start_date from API
  // KGH-795 / APS-255 item 11: how many college programs have said they will be
  // at one of these. Counted across tournaments, not summed across programs, so
  // one school going to three of them is not reported as three.
  const eventsWithCoaches = tournaments.filter((t) => (t.coaches_attending || 0) > 0).length;

  return (
    <>
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.headerLeft}>
            <Calendar size={20} style={styles.headerIcon} />
            <h2 style={styles.sectionTitle}>Tournament Schedule</h2>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            style={styles.viewAllButton}
          >
            View All
            <ChevronRight size={16} style={{ marginLeft: '4px' }} />
          </button>
        </div>

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Loading tournaments...</p>
          </div>
        ) : (
          <div style={styles.content}>
            {upcomingCount === 0 ? (
              <div style={styles.emptyState}>
                <Calendar size={48} color="#D1D5DB" />
                <p style={styles.emptyTitle}>No Upcoming Tournaments</p>
                <p style={styles.emptyText}>
                  Add tournaments to help coaches find you on the field
                </p>
                <button
                  onClick={() => setModalOpen(true)}
                  style={styles.addButton}
                >
                  Add Tournament
                </button>
              </div>
            ) : (
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>{upcomingCount}</div>
                  <div style={styles.statLabel}>
                    Upcoming Tournament{upcomingCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>{totalGames}</div>
                  <div style={styles.statLabel}>
                    Total Game{totalGames !== 1 ? 's' : ''}
                  </div>
                </div>
                {eventsWithCoaches > 0 && (
                  <div style={styles.statCard}>
                    <div style={{ ...styles.statValue, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Users size={18} aria-hidden="true" />
                      {eventsWithCoaches}
                    </div>
                    <div style={styles.statLabel}>
                      With College Coaches Going
                    </div>
                  </div>
                )}
                {nextTournament && (
                  <div style={styles.nextTournamentCard}>
                    <div style={styles.nextTournamentLabel}>Next Up</div>
                    <div style={styles.nextTournamentName}>{nextTournament.name}</div>
                    <div style={styles.nextTournamentDate}>
                      {new Date(nextTournament.start_date.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {modalOpen && (
        <TournamentListModal
          athleteId={athleteId}
          onClose={() => setModalOpen(false)}
          onUpdate={loadTournaments}
        />
      )}
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    color: '#F9550D',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
  },
  viewAllButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    color: '#F9550D',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #F3F4F6',
    borderTop: '3px solid #F9550D',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#6B7280',
    fontSize: '14px',
    fontFamily: 'Manrope, sans-serif',
  },
  content: {
    minHeight: '150px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '16px 0 8px 0',
    fontFamily: 'Manrope, sans-serif',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 24px 0',
    fontFamily: 'Manrope, sans-serif',
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#F9550D',
    marginBottom: '8px',
    fontFamily: 'Manrope, sans-serif',
  },
  statLabel: {
    fontSize: '14px',
    color: '#6B7280',
    fontFamily: 'Manrope, sans-serif',
  },
  nextTournamentCard: {
    padding: '20px',
    backgroundColor: '#FFF7ED',
    borderRadius: '8px',
    borderLeft: '4px solid #F9550D',
  },
  nextTournamentLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#F9550D',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
    fontFamily: 'Manrope, sans-serif',
  },
  nextTournamentName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: '4px',
    fontFamily: 'Manrope, sans-serif',
  },
  nextTournamentDate: {
    fontSize: '14px',
    color: '#6B7280',
    fontFamily: 'Manrope, sans-serif',
  },
};

export default TournamentSection;
